/**
 * ¿DE QUIÉN CUELGA UN PUESTO O UN PERFIL?
 * =======================================
 *
 * Desde el 02/10/2026 un puesto —y por tanto su perfil— cuelga de **un negocio O de una empresa
 * colaboradora**, nunca de los dos. `tipo_persona` dice cuál de las dos columnas manda y la otra
 * está a NULL. Es la regla de las dos columnas, la misma de `motivo` / `motivo_id`.
 *
 * ── POR QUÉ ESTO VIVE EN UN FICHERO Y NO EN CADA PANTALLA ─────────────────────────────────────
 *
 * Porque la pregunta estaba escrita SIETE veces, copiada:
 *
 *     WizardAlta.jsx          puestos y perfiles      <- el grave
 *     Perfiles.jsx            puestos
 *     Puestos.jsx             el filtro del listado
 *     Avisos.jsx              puestos, dos veces
 *     SolicitarDatosDrawer    puestos
 *
 * y las siete decían `p.negocio_id === Number(negocioId)`. Con `negocio_id` a NULL en los puestos
 * de colaborador, **las siete los dejan fuera sin dar un solo error**: la lista sale vacía y ya.
 * El de `WizardAlta` es el que duele: se crea el puesto de colaborador, se crea su perfil, y al dar
 * el alta no aparecen. Nada que leer en una consola, nada que buscar.
 *
 * Es la cuarta copia-a-mano de la semana —el filtro de secciones de Plantillas, `gestionURL`,
 * `ROLES_QUE_ELIGEN`, los `matchPaths` del menú—. Todas iguales: una fuente que manda y copias que
 * alguien tiene que acordarse de actualizar. Aquí la fuente es la fila, así que la pregunta se hace
 * UNA vez y las pantallas la llaman.
 *
 * ── EL `Number('')` QUE HABRÍA COLADO FILAS ───────────────────────────────────────────────────
 *
 * `Number('')` y `Number(null)` son **0**, y `fila.negocio_id` de un colaborador es NULL, que
 * también vale 0 al convertirlo. Sin el guard de «ámbito sin elegir», un desplegable vacío haría
 * casar 0 con 0 y enseñaría puestos de otro sitio. Por eso un ámbito sin padre no devuelve nada en
 * vez de devolverlo todo: en un filtro, «todavía no he elegido» no es «dámelo todo».
 */

export const INTERNO = 'interno';
export const COLABORADOR = 'colaborador';

/** Un valor de formulario que todavía no se ha elegido. '' y null son lo mismo aquí. */
const sinElegir = (v) => v === null || v === undefined || v === '';

/**
 * ¿La fila (puesto o perfil) pertenece al ámbito que se está mirando?
 *
 * @param {object} fila    puesto o perfil, tal cual lo devuelve el servidor
 * @param {object} ambito  { tipo, negocioId, colaboradoraId } — `tipo` opcional
 * @returns {boolean}
 */
export function perteneceAlAmbito(fila, ambito) {
  if (!fila) return false;
  const { tipo = null, negocioId = null, colaboradoraId = null } = ambito || {};

  /* Las filas anteriores al 02/10 no traen `tipo_persona` en el puesto, y todas eran de interno:
   * así las creó la migración, con `DEFAULT 'interno'`, y así se comprobó releyéndolas. */
  const tipoFila = fila.tipo_persona || INTERNO;
  if (tipo && tipoFila !== tipo) return false;

  if (tipoFila === COLABORADOR) {
    if (sinElegir(colaboradoraId)) return false;
    return Number(fila.empresa_colaboradora_id) === Number(colaboradoraId);
  }
  if (sinElegir(negocioId)) return false;
  return Number(fila.negocio_id) === Number(negocioId);
}

/**
 * Los puestos o perfiles **activos** de un ámbito. Es lo que quiere casi todo desplegable: un
 * puesto desactivado no se puede elegir, y ofrecerlo para que el servidor lo rechace después es
 * enseñar una opción que no existe.
 */
export function activosDelAmbito(filas, ambito) {
  return (filas || []).filter((f) => f.activo && perteneceAlAmbito(f, ambito));
}

/**
 * El ámbito de un alta o de un formulario que ya tiene elegido el tipo de persona.
 * Un alta de colaborador se mira contra su empresa colaboradora; una de interno, contra su negocio.
 */
export function ambitoDe(tipo, { negocioId, colaboradoraId } = {}) {
  return tipo === COLABORADOR
    ? { tipo: COLABORADOR, colaboradoraId }
    : { tipo: INTERNO, negocioId };
}

/**
 * El padre de una fila, para pintarlo. El servidor ya manda `padre` resuelto; esto es el respaldo
 * para lo que todavía lea `negocio` o `colaboradora` por separado.
 */
export function nombreDelPadre(fila) {
  if (!fila) return '';
  if (fila.padre && fila.padre.nombre) return fila.padre.nombre;
  const p = (fila.tipo_persona || INTERNO) === COLABORADOR ? fila.colaboradora : fila.negocio;
  return (p && p.nombre) || '';
}
